
import React, { useState, useEffect } from 'react';
import DashboardSidebar from '../components/DashboardSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search,
  RefreshCw,
  Eye,
  Mail,
  Phone,
  MessageSquare,
  Calendar,
  X,
  User,
  Inbox
} from 'lucide-react';
import { toast } from 'sonner';
import { contactApi } from '../lib/api';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  // Fetch contact list from backend
  const fetchContacts = async () => {
    setLoading(true);

    try {
      const response = await contactApi.getAll();

      const data = response.data;
      setContacts(data.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  // Search and filter
  const filteredContacts = contacts.filter((contact) => {
    const query = searchQuery.toLowerCase();

    const matchesSearch =
      contact.name?.toLowerCase().includes(query) ||
      contact.email?.toLowerCase().includes(query) ||
      contact.phone?.toLowerCase().includes(query) ||
      contact.subject?.toLowerCase().includes(query);

    const matchesStatus =
      statusFilter === 'all' ||
      contact.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';

    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Status badge
  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-700',
      read: 'bg-yellow-100 text-yellow-700',
      replied: 'bg-green-100 text-green-700'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${
        styles[status] || 'bg-gray-100 text-gray-600'
      }`}>
        {status || 'new'}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <DashboardSidebar />

      <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">

          <div>
            <h1
              className="text-2xl lg:text-3xl font-bold text-[#0A1E40]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Manage Contacts
            </h1>

            <p className="text-[#4A5568] mt-1">
              {contacts.length} total contact submissions
            </p>
          </div>

          <Button
            onClick={fetchContacts}
            disabled={loading}
            className="bg-[#FF5A36] hover:bg-[#E0492B]"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">

          <div className="bg-white rounded-xl p-5 border border-[#D1CBBF]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#4A5568]">Total Contacts</p>
                <h2 className="text-2xl font-bold text-[#0A1E40] mt-1">
                  {contacts.length}
                </h2>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Inbox className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-[#D1CBBF]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#4A5568]">New Contacts</p>
                <h2 className="text-2xl font-bold text-[#FF5A36] mt-1">
                  {contacts.filter(c => c.status === 'new').length}
                </h2>
              </div>
              <div className="bg-orange-100 p-3 rounded-lg">
                <MessageSquare className="w-6 h-6 text-[#FF5A36]" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-[#D1CBBF]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#4A5568]">Replied</p>
                <h2 className="text-2xl font-bold text-green-600 mt-1">
                  {contacts.filter(c => c.status === 'replied').length}
                </h2>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <Mail className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">

          <div className="relative flex-1 max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF] pointer-events-none" />

            <Input
              type="text"
              placeholder="Search by name, email, phone or subject..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 bg-white"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 rounded-md border border-[#D1CBBF] bg-white px-4 text-sm"
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="read">Read</option>
            <option value="replied">Replied</option>
          </select>

        </div>

        {/* Contact Table */}
        <div className="bg-white rounded-xl border border-[#D1CBBF] overflow-hidden">

          <div className="overflow-x-auto">
            <table className="data-table w-full">

              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Phone</th>
                  <th>Subject</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>

                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10">
                      <div className="animate-pulse text-[#4A5568]">
                        Loading contacts...
                      </div>
                    </td>
                  </tr>
                ) : filteredContacts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12">
                      <Inbox className="w-10 h-10 mx-auto mb-3 text-gray-300" />

                      <p className="text-[#4A5568] font-medium">
                        No contacts found
                      </p>

                      <p className="text-sm text-gray-400 mt-1">
                        Try changing your search or filter.
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredContacts.map((contact) => (
                    <tr key={contact.contact_id}>

                      <td>
                        <div className="font-semibold text-[#0A1E40]">
                          {contact.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {contact.email}
                        </div>
                      </td>

                      <td>{contact.phone || 'N/A'}</td>

                      <td className="max-w-[200px] truncate">
                        {contact.subject}
                      </td>

                      <td className="text-sm">
                        {formatDate(contact.created_at)}
                      </td>

                      <td>
                        {getStatusBadge(contact.status)}
                      </td>

                      <td>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedContact(contact)}
                          className="text-[#0A1E40]"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </td>

                    </tr>
                  ))
                )}

              </tbody>
            </table>
          </div>
        </div>

        {/* Contact Details Modal */}
        {selectedContact && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">

            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">

              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-[#E8E2D6]">

                <div>
                  <h2 className="text-xl font-bold text-[#0A1E40]">
                    Contact Details
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    ID: {selectedContact.contact_id}
                  </p>
                </div>

                <button
                  onClick={() => setSelectedContact(null)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>

              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-5">

                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>

                  <div>
                    <p className="text-xs text-gray-500">Customer Name</p>
                    <p className="font-semibold text-[#0A1E40]">
                      {selectedContact.name}
                    </p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">

                  <div className="flex items-center gap-3 bg-[#F5F0E6] rounded-lg p-4">
                    <Mail className="w-5 h-5 text-[#FF5A36]" />

                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">Email</p>
                      <a
                        href={`mailto:${selectedContact.email}`}
                        className="text-sm font-medium text-[#0A1E40] break-all"
                      >
                        {selectedContact.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 bg-[#F5F0E6] rounded-lg p-4">
                    <Phone className="w-5 h-5 text-[#FF5A36]" />

                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <a
                        href={`tel:${selectedContact.phone}`}
                        className="text-sm font-medium text-[#0A1E40]"
                      >
                        {selectedContact.phone || 'N/A'}
                      </a>
                    </div>
                  </div>

                </div>

                <div className="bg-[#F5F0E6] rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Subject</p>
                  <p className="font-semibold text-[#0A1E40]">
                    {selectedContact.subject}
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4 text-[#FF5A36]" />
                    <p className="font-semibold text-[#0A1E40]">
                      Customer Message
                    </p>
                  </div>

                  <div className="bg-[#F5F0E6] rounded-lg p-4 text-sm text-[#4A5568] whitespace-pre-wrap break-words">
                    {selectedContact.message}
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-[#E8E2D6]">

                  <div>
                    <p className="text-xs text-gray-500 mb-1">
                      Submitted On
                    </p>

                    <div className="flex items-center gap-2 text-sm text-[#0A1E40]">
                      <Calendar className="w-4 h-4" />
                      {formatDate(selectedContact.created_at)}
                    </div>
                  </div>

                  <div>
                    {getStatusBadge(selectedContact.status)}
                  </div>

                </div>

              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-[#E8E2D6] flex justify-end gap-3">

                <Button
                  variant="outline"
                  onClick={() => setSelectedContact(null)}
                >
                  Close
                </Button>

                <Button
                  onClick={() => {
                    window.location.href =
                      `mailto:${selectedContact.email}?subject=${encodeURIComponent(
                        'Re: ' + selectedContact.subject
                      )}`;
                  }}
                  className="bg-[#FF5A36] hover:bg-[#E0492B]"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Reply via Email
                </Button>

              </div>

            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default AdminContacts;
